/**
 * blog-50-30-20.jsx
 *
 * Article “evergreen” pour attirer du trafic SEO sur un mot-clé très recherché.
 * Objectif : contenu utile, structuré (H1/H2), et maillage vers le simulateur.
 */

import { Link } from "react-router-dom";
import { useEffect } from "react";
import { useSEO } from "../seo/useSEO";
import "./blog.css";

function injectArticleJsonLd() {
  // Injecte/refresh un JSON-LD Article. On le met à jour à chaque montage.
  const id = "howmuch-jsonld-article";
  const existing = document.getElementById(id);
  if (existing) existing.remove();

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: "La règle 50/30/20 : structurer son budget simplement",
    author: { "@type": "Organization", name: "HowMuch" },
    publisher: { "@type": "Organization", name: "HowMuch" },
    datePublished: "2026-01-13",
    dateModified: "2026-01-13",
    inLanguage: "fr-FR",
    mainEntityOfPage: "https://how-much.app/blog/regle-50-30-20",
  };

  const script = document.createElement("script");
  script.type = "application/ld+json";
  script.id = id;
  script.text = JSON.stringify(jsonLd);
  document.head.appendChild(script);
}

export default function BlogRule503020() {
  useSEO({
    title: "Règle 50/30/20 : méthode budget + exemple | HowMuch",
    description:
      "Comprends la règle 50/30/20 pour répartir ton revenu : 50% besoins, 30% envies, 20% épargne/dettes. Exemple concret et conseils.",
    canonicalPath: "/blog/regle-50-30-20",
    ogType: "article",
  });

  useEffect(() => {
    injectArticleJsonLd();
  }, []);

  return (
    <main className="bl">
      <header className="bl-header">
        <div className="bl-container bl-header-inner">
          <Link to="/" className="bl-brand" aria-label="Retour à l’accueil HowMuch">
            <div className="bl-logo">HM</div>
            <div>
              <div className="bl-brand-name">HowMuch</div>
              <div className="bl-brand-tag">Blog finance personnelle</div>
            </div>
          </Link>

          <nav className="bl-nav" aria-label="Navigation principale">
            <Link className="bl-link" to="/simulate">
              Simulateur
            </Link>
            <Link className="bl-link" to="/blog">
              Blog
            </Link>
            <Link className="bl-link" to="/about">
              À propos
            </Link>
          </nav>
        </div>
      </header>

      <article className="bl-container bl-article">
        <div className="bl-breadcrumbs">
          <Link to="/blog">← Retour au blog</Link>
        </div>

        <h1>La règle 50/30/20 : structurer son budget simplement</h1>
        <p className="bl-lead">
          La règle <strong>50/30/20</strong> est une méthode simple pour répartir ton revenu mensuel entre
          <strong> besoins</strong>, <strong>envies</strong> et <strong>épargne / dettes</strong>.
        </p>

        <section>
          <h2>Le principe</h2>
          <ul>
            <li>
              <strong>50%</strong> pour les <strong>besoins</strong> (logement, courses, transport, factures).
            </li>
            <li>
              <strong>30%</strong> pour les <strong>envies</strong> (restaurants, loisirs, shopping, abonnements).
            </li>
            <li>
              <strong>20%</strong> pour <strong>épargne / dettes</strong> (épargne de sécurité, investissement,
              remboursement anticipé).
            </li>
          </ul>
        </section>

        <section>
          <h2>Exemple chiffré</h2>
          <p>
            Si ton revenu net mensuel est de <strong>2 500 €</strong> :
          </p>

          <div className="bl-table" role="table" aria-label="Exemple de répartition 50/30/20">
            <div className="bl-tr bl-th" role="row">
              <div role="columnheader">Catégorie</div>
              <div role="columnheader">Pourcentage</div>
              <div role="columnheader">Montant</div>
            </div>
            <div className="bl-tr" role="row">
              <div role="cell">Besoins</div>
              <div role="cell">50%</div>
              <div role="cell">1 250 €</div>
            </div>
            <div className="bl-tr" role="row">
              <div role="cell">Envies</div>
              <div role="cell">30%</div>
              <div role="cell">750 €</div>
            </div>
            <div className="bl-tr" role="row">
              <div role="cell">Épargne / dettes</div>
              <div role="cell">20%</div>
              <div role="cell">500 €</div>
            </div>
          </div>

          <p className="bl-note">
            Le but n’est pas d’être “parfait”, mais d’avoir un cadre simple pour décider où va ton argent.
          </p>
        </section>

        <section>
          <h2>Comment l’adapter à ta situation</h2>
          <ul>
            <li>Si ton logement est cher, vise <strong>60/20/20</strong> temporairement.</li>
            <li>Si tu veux accélérer un objectif, vise <strong>50/20/30</strong> (plus d’épargne).</li>
            <li>Si tu rembourses des dettes, le “20%” peut d’abord être du remboursement.</li>
          </ul>
        </section>

        <section>
          <h2>Passer à l’action</h2>
          <p>
            Fixe un objectif (épargne de sécurité, apport, voyage), puis teste une mensualité réaliste.
            HowMuch t’aide à visualiser <strong>le délai</strong>.
          </p>
          <Link className="bl-btn" to="/simulate">
            Tester une simulation →
          </Link>
        </section>
      </article>

      <footer className="bl-footer">
        <div className="bl-container bl-footer-inner">
          <span>© {new Date().getFullYear()} HowMuch</span>
          <Link className="bl-link" to="/blog">
            Voir tous les articles
          </Link>
        </div>
      </footer>
    </main>
  );
}
